import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface DispatcherScore {
  id: string;
  load_id: string;
  user_id: string;
  score: number;
  verdict: "call_now" | "worth_watching" | "negotiate_higher" | "weak_market" | "skip";
  reasoning: string | null;
  factors: Record<string, unknown> | null;
  source: "manual" | "watch_match" | "demo";
  watch_id: string | null;
  load_updated_at_snapshot: string | null;
  scored_at: string;
}

export const VERDICT_LABEL: Record<DispatcherScore["verdict"], string> = {
  call_now: "CALL NOW",
  worth_watching: "WORTH WATCHING",
  negotiate_higher: "NEGOTIATE HIGHER",
  weak_market: "WEAK MARKET",
  skip: "SKIP",
};

export function scoreColor(score: number): string {
  if (score >= 80) return "text-brand-teal";
  if (score >= 60) return "text-amber-500";
  return "text-muted-foreground";
}

export function scoreBg(score: number): string {
  if (score >= 80) return "bg-brand-teal/10 border-brand-teal/30";
  if (score >= 60) return "bg-amber-500/10 border-amber-500/30";
  return "bg-muted/40 border-border";
}

/** Fetch cached score for one load (does not spend credits). */
export function useCachedScore(loadId: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [score, setScore] = useState<DispatcherScore | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!loadId || !userId) {
      setScore(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    supabase
      .from("dispatcher_load_scores")
      .select("*")
      .eq("load_id", loadId)
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) {
          setScore((data as DispatcherScore) || null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadId, userId]);

  return { score, loading, setScore };
}

/** Mutating action — spends 1 credit unless cached and fresh. */
export function useScoreAction() {
  const [scoring, setScoring] = useState(false);

  const score = useCallback(async (loadId: string, opts?: { force?: boolean; watchId?: string | null }) => {
    setScoring(true);
    try {
      const { data, error } = await supabase.functions.invoke("dispatcher-score", {
        body: { load_id: loadId, force: opts?.force, watch_id: opts?.watchId ?? null, source: "manual" },
      });
      if (error) throw error;
      if (data?.error) {
        if (data?.credits_required) {
          toast.error("Not enough credits. Top up to use ATLAS scoring.");
        } else {
          toast.error(data.error);
        }
        return null;
      }
      return data as DispatcherScore & { cached?: boolean };
    } catch (e: any) {
      toast.error(e?.message || "Score failed");
      return null;
    } finally {
      setScoring(false);
    }
  }, []);

  return { score, scoring };
}

/** Bulk cached fetch for table badges. */
export function useCachedScores(loadIds: string[]) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [scores, setScores] = useState<Record<string, DispatcherScore>>({});

  useEffect(() => {
    if (!userId || loadIds.length === 0) {
      setScores({});
      return;
    }
    let cancelled = false;
    supabase
      .from("dispatcher_load_scores")
      .select("*")
      .eq("user_id", userId)
      .in("load_id", loadIds)
      .then(({ data }) => {
        if (cancelled) return;
        const map: Record<string, DispatcherScore> = {};
        ((data as DispatcherScore[]) || []).forEach((s) => {
          map[s.load_id] = s;
        });
        setScores(map);
      });
    return () => {
      cancelled = true;
    };
    // join load ids into a stable key
  }, [userId, loadIds.join(",")]);

  return scores;
}
